/**
 * Stats Service
 * 
 * Query functions for admin statistics dashboard.
 * Uses audit logs with 5-minute caching.
 */

import { auth, db } from './firebase';
import { collection, query, where, getDocs, Timestamp, getCountFromServer } from 'firebase/firestore';

// Cache configuration
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const HOURLY_CACHE_TTL = 60 * 1000; // 1 minute for live "Today" view

/**
 * Get cached data or fetch fresh
 */
const getCachedOrFetch = async (key, fetchFn) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  const data = await fetchFn();
  cache.set(key, { data, timestamp: Date.now() });
  return data;
};

/**
 * Get cached data or fetch fresh, using the short TTL for live hourly data
 */
const getHourlyCachedOrFetch = async (key, fetchFn) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < HOURLY_CACHE_TTL) {
    return cached.data;
  }
  
  const data = await fetchFn();
  cache.set(key, { data, timestamp: Date.now() });
  return data;
};

/**
 * Build an array of 24 hourly buckets ending at the current hour.
 * Each bucket: { hour: 0-23, label: '2 PM', count: 0, dateKey: 'YYYY-M-D-H' }
 */
const buildHourlyBuckets = () => {
  const buckets = [];
  const now = new Date();
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 60 * 60 * 1000);
    const hour = d.getHours();
    const label = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${hour}`;
    buckets.push({ hour, label, count: 0, dateKey });
  }
  return buckets;
};

/**
 * Convert a JS Date to the same dateKey shape used in buildHourlyBuckets
 */
const toHourKey = (date) =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

/**
 * Generic hourly bucket query for audit log events in the last 24 hours.
 * @param {string|null} action - Audit log action filter, or null for any action
 * @param {string|null} companyId - Company filter, or null for all
 * @param {'count'|'uniqueUsers'} aggregate - How to aggregate each bucket
 */
const getEventsByHour = async (action, companyId, aggregate = 'count') => {
  const startTimestamp = Timestamp.fromDate(
    new Date(Date.now() - 24 * 60 * 60 * 1000)
  );
  const auditRef = collection(db, 'auditLogs');
  
  const filters = [where('timestamp', '>=', startTimestamp)];
  if (action) filters.push(where('action', '==', action));
  if (companyId) filters.push(where('companyId', '==', companyId));
  
  const q = query(auditRef, ...filters);
  const snapshot = await getDocs(q);
  
  const buckets = buildHourlyBuckets();
  const indexByKey = new Map(buckets.map((b, i) => [b.dateKey, i]));
  const uniqueUsersByBucket = aggregate === 'uniqueUsers'
    ? buckets.map(() => new Set())
    : null;
  
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (!data.timestamp) return;
    const date = data.timestamp.toDate();
    const key = toHourKey(date);
    const idx = indexByKey.get(key);
    if (idx === undefined) return;
    
    if (aggregate === 'uniqueUsers') {
      if (data.userId) uniqueUsersByBucket[idx].add(data.userId);
    } else {
      buckets[idx].count += 1;
    }
  });
  
  if (aggregate === 'uniqueUsers') {
    uniqueUsersByBucket.forEach((set, idx) => {
      buckets[idx].count = set.size;
    });
  }
  
  return buckets;
};

/**
 * Get start timestamp for a time period
 */
const getStartTimestamp = (period) => {
  const now = new Date();
  let start;
  
  switch (period) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'month':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    default:
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  
  return Timestamp.fromDate(start);
};

/**
 * Get all companies for the dropdown filter
 */
export const getAllCompanies = async () => {
  const cacheKey = 'all_companies';
  
  return getCachedOrFetch(cacheKey, async () => {
    const companiesRef = collection(db, 'companies');
    const snapshot = await getDocs(companiesRef);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,  // Must come AFTER spread to ensure doc.id is used
      };
    });
  });
};

/**
 * Get documents created count for a period
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getDocumentsCreated = async (period, companyId = null) => {
  const cacheKey = `docs_created_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'document.create'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'document.create'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  });
};

/**
 * Get documents created per day for charting
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getDocumentsCreatedByDay = async (period, companyId = null) => {
  const cacheKey = `docs_by_day_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'document.create'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'document.create'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    // Group by day
    const byDay = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.timestamp) {
        const date = data.timestamp.toDate();
        const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        byDay[dayKey] = (byDay[dayKey] || 0) + 1;
      }
    });
    
    // Fill in missing days with zeros
    const days = period === 'today' ? 1 : period === 'week' ? 7 : 30;
    const result = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayKey = date.toISOString().split('T')[0];
      result.push({
        date: dayKey,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: byDay[dayKey] || 0,
      });
    }
    
    return result;
  });
};

/**
 * Get active users count for a period (unique userIds with any action)
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getActiveUsers = async (period, companyId = null) => {
  const cacheKey = `active_users_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    // Get unique userIds
    const userIds = new Set();
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.userId) {
        userIds.add(data.userId);
      }
    });
    
    return userIds.size;
  });
};

/**
 * Get total users count
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getTotalUsers = async (companyId = null) => {
  const usersRef = collection(db, 'users');
  const q = companyId
    ? query(usersRef, where('companyId', '==', companyId))
    : usersRef;
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

/**
 * Get aggregate totals plus patient creation series from the authenticated
 * backend. Patient records, rather than best-effort audit events, are the
 * source of truth for patient creation.
 */
export const getAdminUsageSummary = async (period, companyId = null) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Authentication required');

  const params = new URLSearchParams({
    period,
    timezoneOffsetMinutes: String(new Date().getTimezoneOffset()),
  });
  if (companyId) params.set('companyId', companyId);

  const token = await currentUser.getIdToken();
  const response = await fetch(`/api/v3/admin/usage-summary?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Usage summary request failed (${response.status})`);
  }

  const data = await response.json();
  // An older/incomplete response is unavailable, not evidence of zero activity.
  if (!Array.isArray(data?.documentTypeBreakdown) || !Array.isArray(data?.documentCategoryBreakdown)) {
    throw new Error('Document activity breakdown is missing from the usage summary');
  }

  const categoryKeys = new Set(['initial', 'progress', 'summary', 'other']);
  return {
    patientCount: Number(data.patientCount) || 0,
    documentCount: data.documentCount == null ? null : Number(data.documentCount) || 0,
    sharedPatientCount: Number(data.sharedPatientCount) || 0,
    patientsCreated: Number(data.patientsCreated) || 0,
    patientsCreatedByDay: Array.isArray(data.patientsCreatedByDay) ? data.patientsCreatedByDay : [],
    patientsCreatedByHour: Array.isArray(data.patientsCreatedByHour) ? data.patientsCreatedByHour : [],
    documentsCreated: Number(data.documentsCreated) || 0,
    documentsCreatedByDay: Array.isArray(data.documentsCreatedByDay) ? data.documentsCreatedByDay : [],
    documentsCreatedByHour: Array.isArray(data.documentsCreatedByHour) ? data.documentsCreatedByHour : [],
    documentCategoryBreakdown: Array.isArray(data.documentCategoryBreakdown)
      ? data.documentCategoryBreakdown
          .filter((item) => categoryKeys.has(item?.key))
          .map((item) => ({
            key: item.key,
            label: String(item.label || item.key),
            count: Number(item.count) || 0,
          }))
      : [],
    documentTypeBreakdown: Array.isArray(data.documentTypeBreakdown)
      ? data.documentTypeBreakdown.map((item) => ({
          typeId: item?.typeId == null ? null : String(item.typeId),
          name: String(item?.name || 'Unknown document type'),
          category: categoryKeys.has(item?.category) ? item.category : 'other',
          count: Number(item?.count) || 0,
        }))
      : [],
    documentingUsers: Number(data.documentingUsers) || 0,
    patientsDocumented: Number(data.patientsDocumented) || 0,
  };
};

/**
 * Load minimum-necessary statistics for the authenticated Company Admin.
 * Company scope is derived by the backend from the caller's user record.
 */
export const getCompanyAdminUsageSummary = async (period) => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Authentication required');

  const params = new URLSearchParams({
    period,
    timezoneOffsetMinutes: String(new Date().getTimezoneOffset()),
  });
  const token = await currentUser.getIdToken();
  const response = await fetch(`/api/v3/company-admin/usage-summary?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Company usage summary request failed (${response.status})`);
  }

  const data = await response.json();
  const categoryKeys = new Set(['initial', 'progress', 'summary', 'other']);
  return {
    documentsCreated: Number(data.documentsCreated) || 0,
    documentsCreatedByDay: Array.isArray(data.documentsCreatedByDay)
      ? data.documentsCreatedByDay
      : [],
    activeUsers: Number(data.activeUsers) || 0,
    totalUsers: Number(data.totalUsers) || 0,
    streamingStats: {
      avgDurationMin: Number(data.streamingStats?.avgDurationMin) || 0,
      sessionCount: Number(data.streamingStats?.sessionCount) || 0,
    },
    activeUsersByDay: Array.isArray(data.activeUsersByDay) ? data.activeUsersByDay : [],
    sessionsByDay: Array.isArray(data.sessionsByDay) ? data.sessionsByDay : [],
    documentCategoryBreakdown: Array.isArray(data.documentCategoryBreakdown)
      ? data.documentCategoryBreakdown
          .filter((item) => categoryKeys.has(item?.key))
          .map((item) => ({
            key: item.key,
            label: String(item.label || item.key),
            count: Number(item.count) || 0,
          }))
      : [],
    documentTypeBreakdown: Array.isArray(data.documentTypeBreakdown)
      ? data.documentTypeBreakdown.map((item) => ({
          typeId: item?.typeId == null ? null : String(item.typeId),
          name: String(item?.name || 'Unknown document type'),
          category: categoryKeys.has(item?.category) ? item.category : 'other',
          count: Number(item?.count) || 0,
        }))
      : [],
    documentingUsers: Number(data.documentingUsers) || 0,
    patientsDocumented: Number(data.patientsDocumented) || 0,
  };
};

const normalizeUserDocumentActivity = (data) => {
  const activity = {};
  for (const [activityUserId, values] of Object.entries(data.activity || {})) {
    activity[activityUserId] = {
      documentsCreated: Number(values?.documentsCreated) || 0,
      patientsDocumented: Number(values?.patientsDocumented) || 0,
      lastDocumentAt: values?.lastDocumentAt ? String(values.lastDocumentAt) : null,
    };
  }
  return activity;
};

/**
 * Aggregate weekly document activity for Admin Users tooltips. One cached,
 * aggregate-only request replaces per-row or per-hover Firestore queries.
 */
export const getAdminUserDocumentActivity = async () => {
  return getCachedOrFetch('admin_user_document_activity_week', async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Authentication required');

    const params = new URLSearchParams({
      timezoneOffsetMinutes: String(new Date().getTimezoneOffset()),
    });
    const token = await currentUser.getIdToken();
    const response = await fetch(`/api/v3/admin/user-document-activity?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || `User activity request failed (${response.status})`);
    }

    return normalizeUserDocumentActivity(await response.json());
  });
};

/**
 * Aggregate weekly document activity for users in the authenticated Company
 * Admin's organization. Company scope is derived by the backend.
 */
export const getCompanyAdminUserDocumentActivity = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Authentication required');

  const params = new URLSearchParams({
    timezoneOffsetMinutes: String(new Date().getTimezoneOffset()),
  });
  const token = await currentUser.getIdToken();
  const response = await fetch(`/api/v3/company-admin/user-document-activity?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Company user activity request failed (${response.status})`);
  }

  return normalizeUserDocumentActivity(await response.json());
};

/**
 * Get streaming session stats for a period
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 * @returns {{ avgDurationMin: number, sessionCount: number }}
 */
export const getStreamingStats = async (period, companyId = null) => {
  const cacheKey = `streaming_stats_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    let totalDurationMs = 0;
    let sessionCount = 0;
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.metadata?.durationMs) {
        totalDurationMs += data.metadata.durationMs;
        sessionCount++;
      }
    });
    
    const avgDurationMin = sessionCount > 0 
      ? (totalDurationMs / sessionCount / 60000).toFixed(1) 
      : 0;
    
    return { avgDurationMin: parseFloat(avgDurationMin), sessionCount };
  });
};

/**
 * Clear all cached stats
 */
export const clearStatsCache = () => {
  cache.clear();
};

/**
 * Get active users per day for charting
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getActiveUsersByDay = async (period, companyId = null) => {
  const cacheKey = `users_by_day_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    // Group unique users by day
    const byDay = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.timestamp && data.userId) {
        const date = data.timestamp.toDate();
        const dayKey = date.toISOString().split('T')[0];
        if (!byDay[dayKey]) byDay[dayKey] = new Set();
        byDay[dayKey].add(data.userId);
      }
    });
    
    // Fill in missing days with zeros
    const days = period === 'today' ? 1 : period === 'week' ? 7 : 30;
    const result = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayKey = date.toISOString().split('T')[0];
      result.push({
        date: dayKey,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: byDay[dayKey]?.size || 0,
      });
    }
    
    return result;
  });
};

/**
 * Get streaming sessions per day for charting
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getSessionsByDay = async (period, companyId = null) => {
  const cacheKey = `sessions_by_day_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    // Group by day
    const byDay = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.timestamp) {
        const date = data.timestamp.toDate();
        const dayKey = date.toISOString().split('T')[0];
        byDay[dayKey] = (byDay[dayKey] || 0) + 1;
      }
    });
    
    // Fill in missing days with zeros
    const days = period === 'today' ? 1 : period === 'week' ? 7 : 30;
    const result = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayKey = date.toISOString().split('T')[0];
      result.push({
        date: dayKey,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: byDay[dayKey] || 0,
      });
    }
    
    return result;
  });
};

/**
 * Get total recording minutes for a period
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getTotalRecordingMinutes = async (period, companyId = null) => {
  const cacheKey = `recording_mins_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'streaming.stop'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    let totalDurationMs = 0;
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.metadata?.durationMs) {
        totalDurationMs += data.metadata.durationMs;
      }
    });
    
    // Return total minutes (rounded to 1 decimal)
    return Math.round(totalDurationMs / 60000 * 10) / 10;
  });
};

/**
 * Get documents exported count for a period
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getDocumentsExported = async (period, companyId = null) => {
  const cacheKey = `docs_exported_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'document.export'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'document.export'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  });
};

/**
 * Get documents exported per day for charting
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getDocumentsExportedByDay = async (period, companyId = null) => {
  const cacheKey = `exports_by_day_${companyId || 'all'}_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    let q;
    if (companyId) {
      q = query(
        auditRef,
        where('action', '==', 'document.export'),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTimestamp)
      );
    } else {
      q = query(
        auditRef,
        where('action', '==', 'document.export'),
        where('timestamp', '>=', startTimestamp)
      );
    }
    
    const snapshot = await getDocs(q);
    
    // Group by day
    const byDay = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.timestamp) {
        const date = data.timestamp.toDate();
        const dayKey = date.toISOString().split('T')[0];
        byDay[dayKey] = (byDay[dayKey] || 0) + 1;
      }
    });
    
    // Fill in missing days with zeros
    const days = period === 'today' ? 1 : period === 'week' ? 7 : 30;
    const result = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayKey = date.toISOString().split('T')[0];
      result.push({
        date: dayKey,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: byDay[dayKey] || 0,
      });
    }
    
    return result;
  });
};

/**
 * Get failed processing jobs for a period
 * @param {string} period - 'today' | 'week' | 'month'
 * @param {string|null} companyId - Filter by company, or null for all
 */
export const getFailedJobs = async (period, companyId = null) => {
  const cacheKey = `failed_jobs_${companyId || 'all'}_${period}`;

  return getCachedOrFetch(cacheKey, async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Authentication required');

    const params = new URLSearchParams({ period });
    params.set('timezoneOffsetMinutes', String(new Date().getTimezoneOffset()));
    if (companyId) params.set('companyId', companyId);
    const token = await currentUser.getIdToken();
    const response = await fetch(`/api/v3/admin/processing-failures?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || `Failed-job summary request failed (${response.status})`);
    }

    const data = await response.json();
    return {
      total: Number(data.total) || 0,
      recent: (data.recent || []).map((job) => ({
        ...job,
        createdAt: job.createdAt ? new Date(job.createdAt) : null,
      })),
    };
  });
};

/**
 * Get login failures for a period
 * @param {string} period - 'today' | 'week' | 'month'
 */
export const getLoginFailures = async (period) => {
  const cacheKey = `login_failures_${period}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    const startTimestamp = getStartTimestamp(period);
    const auditRef = collection(db, 'auditLogs');
    
    const q = query(
      auditRef,
      where('action', '==', 'auth.login.failure'),
      where('timestamp', '>=', startTimestamp)
    );
    
    const snapshot = await getDocs(q);
    const failures = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      failures.push({
        id: doc.id,
        userEmail: data.userEmail || data.metadata?.email || 'Unknown',
        errorCode: data.metadata?.errorCode || 'unknown',
        timestamp: data.timestamp?.toDate?.() || null,
      });
    });
    
    return failures;
  });
};

// ----------------------------------------------------------------------------
// Hourly (Today live view) — last 24 hours, bucketed by hour
// Uses HOURLY_CACHE_TTL (1 minute) so the chart feels live.
// ----------------------------------------------------------------------------

/**
 * Documents created per hour (last 24 hours)
 */
export const getDocumentsCreatedByHour = async (companyId = null) => {
  const cacheKey = `docs_by_hour_${companyId || 'all'}`;
  return getHourlyCachedOrFetch(cacheKey, () =>
    getEventsByHour('document.create', companyId, 'count')
  );
};

/**
 * Active (unique) users per hour (last 24 hours)
 */
export const getActiveUsersByHour = async (companyId = null) => {
  const cacheKey = `users_by_hour_${companyId || 'all'}`;
  return getHourlyCachedOrFetch(cacheKey, () =>
    getEventsByHour(null, companyId, 'uniqueUsers')
  );
};

/**
 * Streaming sessions per hour (last 24 hours)
 */
export const getSessionsByHour = async (companyId = null) => {
  const cacheKey = `sessions_by_hour_${companyId || 'all'}`;
  return getHourlyCachedOrFetch(cacheKey, () =>
    getEventsByHour('streaming.stop', companyId, 'count')
  );
};

/**
 * Documents exported per hour (last 24 hours)
 */
export const getDocumentsExportedByHour = async (companyId = null) => {
  const cacheKey = `exports_by_hour_${companyId || 'all'}`;
  return getHourlyCachedOrFetch(cacheKey, () =>
    getEventsByHour('document.export', companyId, 'count')
  );
};

/**
 * Recently active users — distinct userIds in audit logs within the last N minutes.
 * Not cached (we want fresh values for the live counter).
 * @param {string|null} companyId
 * @param {number} windowMinutes - default 5 minutes
 */
export const getRecentlyActiveUsers = async (companyId = null, windowMinutes = 5) => {
  const startTimestamp = Timestamp.fromDate(
    new Date(Date.now() - windowMinutes * 60 * 1000)
  );
  const auditRef = collection(db, 'auditLogs');
  
  const filters = [where('timestamp', '>=', startTimestamp)];
  if (companyId) filters.push(where('companyId', '==', companyId));
  
  const q = query(auditRef, ...filters);
  const snapshot = await getDocs(q);
  
  const userIds = new Set();
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (data.userId) userIds.add(data.userId);
  });
  
  return userIds.size;
};

/**
 * Currently streaming users — distinct userIds with a `streaming.start` event
 * in the last `lookbackHours` hours that has no matching `streaming.stop`
 * (matched by metadata.sessionId, falling back to resourceId).
 * @param {string|null} companyId
 * @param {number} lookbackHours - default 6 hours
 * @returns {Promise<Set<string>>}
 */
export const getCurrentlyStreamingUserIds = async (companyId = null, lookbackHours = 6) => {
  const startTimestamp = Timestamp.fromDate(
    new Date(Date.now() - lookbackHours * 60 * 60 * 1000)
  );
  const auditRef = collection(db, 'auditLogs');
  
  const buildFilters = (action) => {
    const f = [where('action', '==', action), where('timestamp', '>=', startTimestamp)];
    if (companyId) f.push(where('companyId', '==', companyId));
    return f;
  };
  
  const [startsSnap, stopsSnap] = await Promise.all([
    getDocs(query(auditRef, ...buildFilters('streaming.start'))),
    getDocs(query(auditRef, ...buildFilters('streaming.stop'))),
  ]);
  
  // Session keys that have a matching stop
  const stoppedKeys = new Set();
  stopsSnap.forEach((doc) => {
    const data = doc.data();
    const key = data.metadata?.sessionId || data.resourceId;
    if (key) stoppedKeys.add(key);
  });
  
  // Any start without a matching stop = still streaming
  const activeUserIds = new Set();
  startsSnap.forEach((doc) => {
    const data = doc.data();
    const key = data.metadata?.sessionId || data.resourceId;
    if (data.userId && key && !stoppedKeys.has(key)) {
      activeUserIds.add(data.userId);
    }
  });
  
  return activeUserIds;
};

/**
 * Combined live presence: users active in the last `windowMinutes` minutes UNION
 * users currently mid-stream (start with no matching stop in the last 6 hours).
 * Not cached.
 * @param {string|null} companyId
 * @param {number} windowMinutes - default 5
 * @returns {Promise<{total: number, recentlyActive: number, streaming: number}>}
 */
export const getLivePresence = async (companyId = null, windowMinutes = 5) => {
  const startTimestamp = Timestamp.fromDate(
    new Date(Date.now() - windowMinutes * 60 * 1000)
  );
  const auditRef = collection(db, 'auditLogs');
  
  const filters = [where('timestamp', '>=', startTimestamp)];
  if (companyId) filters.push(where('companyId', '==', companyId));
  
  const [recentSnap, streamingUserIds] = await Promise.all([
    getDocs(query(auditRef, ...filters)),
    getCurrentlyStreamingUserIds(companyId, 6),
  ]);
  
  const recentUserIds = new Set();
  recentSnap.forEach((doc) => {
    const data = doc.data();
    if (data.userId) recentUserIds.add(data.userId);
  });
  
  const union = new Set([...recentUserIds, ...streamingUserIds]);
  return {
    total: union.size,
    recentlyActive: recentUserIds.size,
    streaming: streamingUserIds.size,
  };
};
